import type { TodayApproval } from './todayData';

/**
 * Today → Went out: one row per page, article, form or the site, however
 * many times it was approved in the window. The row says which version is
 * live and how many times it went out; the versions are behind History.
 */
export interface WentOutGroup {
  key: string;
  /** Newest first. */
  events: TodayApproval[];
  latest: TodayApproval;
  /** The publish that is live now, if one in the window still is. */
  live: TodayApproval | null;
  publishes: number;
}

export function groupApprovals(approvals: TodayApproval[]): WentOutGroup[] {
  const byKey = new Map<string, TodayApproval[]>();
  for (const a of approvals) {
    const key = `${a.kind}:${a.subjectId}`;
    byKey.set(key, [...(byKey.get(key) || []), a]);
  }
  return [...byKey].map(([key, list]) => {
    const events = [...list].sort(
      (a, b) => new Date(b.recordedAt).getTime() - new Date(a.recordedAt).getTime()
    );
    return {
      key,
      events,
      latest: events[0],
      live: events.find((e) => e.action === 'publish' && e.stillLive) || null,
      publishes: events.filter((e) => e.action === 'publish').length,
    };
  });
}

const version = (a: TodayApproval) => (a.versionNumber ? `v${a.versionNumber}` : null);

/** "v17 live · published 6× today", for a group of more than one approval. */
export function groupSummary(g: WentOutGroup, period: string) {
  const live = g.live ? version(g.live) : null;
  const count =
    g.publishes === g.events.length
      ? `published ${g.publishes}× ${period}`
      : `${g.events.length} approvals ${period}`;
  return [live ? `${live} live` : null, count].filter(Boolean).join(' · ');
}

const VERB: Record<string, string> = {
  publish: 'Published',
  schedule: 'Scheduled',
  unpublish: 'Took down',
};

/** One line of a group's History: when, which version, what, and whether it's live. */
export function historyLine(a: TodayApproval) {
  return {
    id: a.id,
    at: new Date(a.recordedAt),
    version: version(a) || '—',
    what: [VERB[a.action] || a.action, a.approvedBy ? `by ${a.approvedBy}` : null]
      .filter(Boolean)
      .join(' '),
    status:
      a.action !== 'publish' ? null : a.stillLive ? ('Live' as const) : ('No longer live' as const),
  };
}

// ── Row words (round 5: every row says what it is and where it lives) ────────

const KIND_NOUN: Record<string, string> = {
  article: 'the article',
  page: 'the page',
  form: 'the form',
  dataset: 'the data for',
};

/**
 * "Published the page “Tips”", "Updated the article “…”", "Took down the form
 * “Tips”", "Published the site’s look and navigation". A publish of a version
 * after the first is an update. `updated` overrides for a group of several.
 */
export function rowText(a: TodayApproval, updated?: boolean): string {
  if (a.kind === 'site') {
    return a.action === 'unpublish'
      ? 'Took down the site’s look and navigation'
      : `${a.action === 'schedule' ? 'Scheduled' : 'Published'} the site’s look and navigation`;
  }
  const isUpdate = updated ?? (a.action === 'publish' && (a.versionNumber || 0) > 1);
  const verb =
    a.action === 'unpublish'
      ? 'Took down'
      : a.action === 'schedule'
        ? 'Scheduled'
        : isUpdate
          ? 'Updated'
          : 'Published';
  const noun = KIND_NOUN[a.kind] || `the ${a.kind}`;
  return a.subjectTitle
    ? `${verb} ${noun} “${a.subjectTitle}”`
    : `${verb} ${noun.replace(/^the /, 'a ')}`;
}

/**
 * Where it lives: "/tips" for a page or article; "on /tips" (or "on /tips and
 * /contact") for a form, which lives on the pages that show it; "not on a live
 * page" for a form no live page shows; "site-wide" for the site's look.
 */
export function rowPlace(a: TodayApproval): string | null {
  if (a.kind === 'site') return 'site-wide';
  if (a.kind === 'form') {
    const on = a.placedOn || [];
    if (!on.length) return 'not on a live page';
    const shown = on.slice(0, 2).join(' and ');
    return `on ${shown}${on.length > 2 ? ` and ${on.length - 2} more` : ''}`;
  }
  return a.subjectPath || null;
}
